// Nachschlage-Quellen der Stammdaten für die Auswahlfelder aller Module (Manifest-Slot `lookups`, web/module.ts):
//   item      Artikel mit Gebinden, Preisvorschlag, Pfand und Bestand am Standort (masterdata.item.lookup)
//   partner   Kunden bzw. Lieferanten – `params.role` nennt die Rolle (masterdata.partner.lookup)
//   barcode   gescannter Code → Artikel und Gebinde (masterdata.barcode.resolve + item.lookup)
// Was der Aufrufer weiß, steht in request.params: role, partnerId, priceMode, date (web/api/README.md).
// Diese Datei wird mit dem Manifest in Node geladen: nur Verträge und Typen importieren, keine Bausteine.
import type { BarcodeLookupHit, ItemLookupEntry, PartnerLookupEntry } from '@api'
import type { OutputOf } from '@shared/contract'
import type { LookupRequest, LookupSource } from '@web/module'
import { barcodeResolve, itemLookup, partnerLookup } from '../contract'

type ItemRow = OutputOf<typeof itemLookup>['rows'][number]
type PartnerRow = OutputOf<typeof partnerLookup>['rows'][number]

const text = (value: unknown): string | undefined => (typeof value === 'string' && value !== '' ? value : undefined)

/** Teil der Eingabe von masterdata.item.lookup, der aus dem Zusammenhang des Auswahlfelds kommt */
function itemContext(request: LookupRequest): { role: 'customer' | 'supplier'; locationId?: string; partnerId?: string; priceMode?: 'gross' | 'net'; date?: string; stock?: false } {
  const priceMode = request.params.priceMode
  const partnerId = text(request.params.partnerId)
  const date = text(request.params.date)
  return {
    role: request.params.role === 'supplier' ? 'supplier' : 'customer',
    ...(request.locationId ? { locationId: request.locationId } : {}),
    ...(partnerId ? { partnerId } : {}),
    ...(priceMode === 'net' || priceMode === 'gross' ? { priceMode } : {}),
    ...(date && /^\d{4}-\d{2}-\d{2}$/.test(date) ? { date } : {}),
    // params.stock === false: Der Server lässt den Bestand weg, was immer die Seite sonst anfragt (Blindzählung)
    ...(request.params.stock === false ? { stock: false as const } : {}),
  }
}

export const toItemEntry = (row: ItemRow): ItemLookupEntry => ({
  id: row.id,
  sku: row.sku,
  name: row.name,
  units: row.units.map((unit) => ({
    id: unit.id,
    short: unit.short,
    label: unit.label,
    baseQty: unit.baseQty,
    isBase: unit.isBase,
    decimals: Math.min(Math.max(unit.decimals, 0), 3) as 0 | 1 | 2 | 3,
    isDefault: unit.isDefault,
    priceCents: unit.priceCents,
    pricePerQty: unit.pricePerQty,
    deposit: unit.deposit,
  })),
  stockBaseQty: row.stockBaseQty,
  taxCodeId: row.taxCodeId,
  taxRateBp: row.taxRateBp,
  lotRequired: row.lotRequired,
  bestBeforeRequired: row.bestBeforeRequired,
  inactive: row.inactive,
})

export const toPartnerEntry = (row: PartnerRow): PartnerLookupEntry => ({
  id: row.id,
  number: row.number,
  name: row.name,
  name2: row.name2,
  addressLines: row.addressLines,
  city: row.city,
  paymentTerms: row.paymentTerms,
  priceGroup: row.priceGroup,
  priceMode: row.priceMode,
  warning: row.warning,
})

export const itemLookupSource: LookupSource<ItemLookupEntry> = {
  permission: itemLookup.permission,
  async search(query, request) {
    const page = await request.run(itemLookup, { ...itemContext(request), ...(query.trim() ? { q: query.trim() } : {}), page: { limit: 20, ...(request.cursor ? { cursor: request.cursor } : {}) } })
    return { items: page.rows.map(toItemEntry), ...(page.nextCursor ? { nextCursor: page.nextCursor } : {}) }
  },
  async byId(id, request) {
    const row = (await request.run(itemLookup, { ...itemContext(request), ids: [id] })).rows[0]
    return row ? toItemEntry(row) : null
  },
}

const partnerRole = (request: LookupRequest): { role?: 'customer' | 'supplier' } => (request.params.role === 'supplier' ? { role: 'supplier' } : request.params.role === 'customer' ? { role: 'customer' } : {})

/** Ohne `permission`: Ob Kunden oder Lieferanten sichtbar sind, entscheidet der Server je Rolle. */
export const partnerLookupSource: LookupSource<PartnerLookupEntry> = {
  async search(query, request) {
    const page = await request.run(partnerLookup, { ...partnerRole(request), ...(query.trim() ? { q: query.trim() } : {}), page: { limit: 20, ...(request.cursor ? { cursor: request.cursor } : {}) } })
    return { items: page.rows.map(toPartnerEntry), ...(page.nextCursor ? { nextCursor: page.nextCursor } : {}) }
  },
  async byId(id, request) {
    const row = (await request.run(partnerLookup, { ...partnerRole(request), ids: [id] })).rows[0]
    return row ? toPartnerEntry(row) : null
  },
}

export const barcodeLookupSource: LookupSource<BarcodeLookupHit> = {
  permission: barcodeResolve.permission,
  search: async () => ({ items: [] }),
  async resolve(code, request) {
    const { hit } = await request.run(barcodeResolve, { code })
    if (!hit) return null
    const row = (await request.run(itemLookup, { ...itemContext(request), ids: [hit.itemId] })).rows[0]
    return row ? { code: hit.code, item: toItemEntry(row), unitId: hit.unitId } : null
  },
}
